import { getOrders } from "../_shared/services/orders.service";
import { get, take, orderBy, pick } from 'lodash';
import { setOrders, ordersWriteSeqNow } from './orders/orders.container';
import { getDataFromGuid } from "../_shared/driver-search";
import { getDriverByEmail, userHasRole, wait } from "../actions/utils";
import { setCurrentUser } from "../authentication/authentication.actions";

// Resolve the order's driver/vehicle/route guids into the `DriverData` shape the
// Chrome extension's METRC form-fill reads (DriverData.{pickup,delivery}.{driver,vehicle}).
// The heavy route.data.rows blob is stripped to keep the persisted slice small.
// The METRC transporter-detail fill reads exactly these fields off each leg
// (ext/src/views/Popup/orders/index.js). Keeping the whole driver/vehicle/route
// records made DriverData roughly 60% of the persisted order payload for no gain.
const slimLeg = (leg) => ({
  driver: pick(get(leg, 'driver'), ['name', 'occLic', 'lic']),
  vehicle: pick(get(leg, 'vehicle'), ['name', 'lic']),
});

export const buildDriverData = (o) => {
  const hasDrivers = get(o, 'data.order.driver');
  if (hasDrivers && Object.keys(hasDrivers).length > 0) {
    const { delivery, pickup } = hasDrivers;

    return {
      delivery: slimLeg(get(hasDrivers, '_delivery', getDataFromGuid(delivery))),
      pickup: slimLeg(get(hasDrivers, '_pickup', getDataFromGuid(pickup))),
    };
  }
  return {};
};

// The exact fields the Chrome extension order list + METRC form-fill consume
// (ext/src/views/Popup/orders/index.js + ext/src/_shared/models/order.js).
// Everything else is dropped so ~400 schedule-day orders fit in localStorage.
const EXT_ORDER_TOP_KEYS = ['id', 'orderId', 'created', 'filledmetrc'];
const EXT_ORDER_DATA_KEYS = [
  // order
  'order.orderType', 'order.orderDef', 'order.transporterLN', 'order.route',
  'order.pickupLN', 'order.destinationLN', 'order.preferredDelivery',
  'order.weight', 'order.notes', 'order.deliveryDate', 'order.pickupDate',
  'order.dropoffDate', 'order.status',
  // billing
  'billing.invoice', 'billing.amount', 'billing.method', 'billing.manifest',
  // manifest marker (dup-submission check + downloaded icon)
  'manifest',
  // companies (PlannedRoute + table columns)
  'pickupCompany.DBA', 'pickupCompany.Licensee', 'pickupCompany.Street Address',
  'pickupCompany.City', 'pickupCompany.Zip',
  'destCompany.DBA', 'destCompany.Licensee', 'destCompany.Licensee DBA',
  'destCompany.Street Address', 'destCompany.City', 'destCompany.Zip',
];

// Project a full order down to only what the extension needs, plus DriverData.
export const projectOrderForExt = (o) => {
  const projected = pick(o, EXT_ORDER_TOP_KEYS);
  projected.data = pick(get(o, 'data', {}), EXT_ORDER_DATA_KEYS);
  projected.DriverData = buildDriverData(o);
  return projected;
};

// The persisted store is a single localStorage key shared with the rest of the
// app, so every writer hands the extension the newest N orders and no more.
// A projected order measures ~1.25KB, so 500 costs ~0.6MB of the browser's
// ~5.2MB budget; the rest of the store is under 5KB now that the user profile
// is slimmed before it is written.
export const EXT_ORDER_LIMIT = 500;

export const projectOrdersForExt = (orders, limit = EXT_ORDER_LIMIT) =>
  take(orderBy(orders || [], 'created', 'desc'), limit).map(o => projectOrderForExt(o));

export const retrieveData = (opts = {}) => async (
  dispatch,
  getState
) => {
  try {
    opts = {
      isMobile: false,
      ...(opts || {}),
    }

    const sState = getState();
    const userId = get(sState, 'user.id');
    const isDriver = userHasRole([2], sState);

    // Snapshot before anything awaits. This pull is the slowest writer of the
    // slice by a wide margin, so any screen-scoped push that starts alongside it
    // lands first — the comparison below is what stops us overwriting one.
    const seqAtStart = ordersWriteSeqNow();

    await wait(); //need to make sure we have the drivers and stuff

    // A driver's orders come from the Schedule screen's own push. This generic
    // pull deliberately fetched none for them but still dispatched the empty
    // result — and since it resolves last, it wiped that push out.
    if (!isDriver) {
      const orders = await getOrders({ userId });

      // A screen-scoped push landed while this account-wide pull was in flight:
      // the Schedules screen's selected day, or the customer Orders list. That
      // writer mirrors what the user is looking at, so it wins. Dispatching here
      // would swap the day on screen for the whole account, which is exactly the
      // bug this guard exists for — the extension then synced every order
      // instead of the selected day (card #101).
      if (ordersWriteSeqNow() === seqAtStart) {
        dispatch(setOrders(projectOrdersForExt(orders, opts.isMobile ? 200 : EXT_ORDER_LIMIT)));
      }
    }

    if (isDriver) {
      const driver = getDriverByEmail(get(sState, 'user.email'));
      const user = {
        ...get(getState(), 'user', {}),
        driver: driver || {},
      }
      dispatch(setCurrentUser(user));
    }
  } catch (err) {
    console.warn(err);
  }
};
